
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

interface Service {
  id: number;
  icon: string;
  title: string;
  description: string;
  features: string[];
  price: string;
  image: string;
}

export default function ServicesSection() {
  const [hoveredService, setHoveredService] = useState<number | null>(null);
  const [visibleCards, setVisibleCards] = useState<number[]>([]);

  const services: Service[] = [
    {
      id: 1,
      icon: 'ri-code-s-slash-line',
      title: 'Web Tasarım & Geliştirme',
      description: 'Modern, responsive ve SEO uyumlu web siteleri ile dijital varlığınızı güçlendirin.',
      features: ['Responsive Tasarım', 'SEO Optimizasyonu', 'Hızlı Yükleme', 'Modern Teknolojiler'],
      price: '4.500₺\'den',
      image: 'https://public.readdy.ai/ai/img_res/5d294988c36e0e69aa0b974d7083a9bb.jpg'
    },
    {
      id: 2,
      icon: 'ri-palette-line',
      title: 'UI/UX Tasarım',
      description: 'Kullanıcı deneyimini merkeze alan, estetik ve fonksiyonel arayüz tasarımları.',
      features: ['Kullanıcı Araştırması', 'Wireframe & Prototip', 'Görsel Tasarım', 'Usability Testi'],
      price: '3.000₺\'den',
      image: 'https://readdy.ai/api/search-image?query=UI%20UX%20design%20workspace%20with%20digital%20tablets%20showing%20modern%20interface%20designs%2C%20color%20palettes%2C%20typography%20samples%2C%20clean%20minimalist%20studio%2C%20blue%20and%20silver%20accents%2C%20professional%20design%20tools%2C%20creative%20atmosphere&width=400&height=300&seq=ui-ux&orientation=landscape'
    },
    {
      id: 3,
      icon: 'ri-shopping-cart-line',
      title: 'E-Ticaret Çözümleri',
      description: 'Güvenli, kullanıcı dostu ve yüksek dönüşüm oranlı e-ticaret platformları.',
      features: ['Güvenli Ödeme', 'Stok Yönetimi', 'Mobil Uyumlu', 'Analytics Entegrasyonu'],
      price: '12.000₺\'den',
      image: 'https://readdy.ai/api/search-image?query=Modern%20e-commerce%20platform%20interface%20on%20multiple%20devices%2C%20online%20shopping%20cart%2C%20product%20displays%2C%20digital%20payment%20systems%2C%20futuristic%20retail%20technology%2C%20clean%20blue%20and%20white%20design%2C%20professional%20commercial%20setup&width=400&height=300&seq=ecommerce&orientation=landscape'
    },
    {
      id: 4,
      icon: 'ri-smartphone-line',
      title: 'Mobil Uygulama Geliştirme',
      description: 'iOS ve Android platformları için native ve cross-platform mobil uygulamalar.',
      features: ['Native Geliştirme', 'Cross-Platform', 'App Store Optimizasyonu', 'Push Notifications'],
      price: '20.000₺\'den',
      image: 'https://readdy.ai/api/search-image?query=Mobile%20app%20development%20workspace%20with%20smartphones%20and%20tablets%20displaying%20modern%20app%20interfaces%2C%20clean%20development%20environment%2C%20blue%20and%20silver%20tech%20aesthetic%2C%20professional%20mobile%20development%20tools%2C%20minimalist%20setup&width=400&height=300&seq=mobile-dev&orientation=landscape'
    },
    {
      id: 5,
      icon: 'ri-search-eye-line',
      title: 'SEO & Dijital Pazarlama',
      description: 'Arama motorlarında üst sıralarda yer almanızı sağlayan optimizasyon hizmetleri.',
      features: ['Anahtar Kelime Analizi', 'İçerik Optimizasyonu', 'Teknik SEO', 'Analytics Raporlama'],
      price: '2.500₺/ay\'dan',
      image: 'https://readdy.ai/api/search-image?query=Digital%20marketing%20analytics%20dashboard%20with%20SEO%20metrics%2C%20search%20engine%20optimization%20tools%2C%20data%20visualization%20charts%2C%20modern%20marketing%20workspace%2C%20blue%20and%20white%20interface%2C%20professional%20marketing%20technology&width=400&height=300&seq=seo-marketing&orientation=landscape'
    },
    {
      id: 6,
      icon: 'ri-shield-check-line',
      title: 'Bakım & Destek',
      description: '7/24 teknik destek ve düzenli bakım hizmetleri ile web sitenizin güvenliği.',
      features: ['7/24 Destek', 'Güvenlik Güncellemeleri', 'Performance Optimizasyonu', 'Yedekleme'],
      price: '500₺/ay\'dan',
      image: 'https://readdy.ai/api/search-image?query=Technical%20support%20and%20maintenance%20center%20with%20multiple%20monitoring%20screens%2C%20server%20maintenance%2C%20security%20systems%2C%20professional%20IT%20support%20environment%2C%20blue%20and%20silver%20tech%20colors%2C%20modern%20support%20infrastructure&width=400&height=300&seq=support&orientation=landscape'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardId = parseInt(entry.target.getAttribute('data-card-id') || '0');
            setVisibleCards(prev => [...prev.filter(id => id !== cardId), cardId]);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('[data-card-id]');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hizmetler" className="py-20 bg-gray-800 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30 mb-6">
            <i className="ri-service-line text-blue-400 mr-2"></i>
            <span className="text-blue-400 text-sm font-medium">Hizmetlerimiz</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Profesyonel Hizmetlerimiz
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Modern teknolojiler ve yaratıcı tasarım anlayışıyla işletmenizin dijital varlığını güçlendiriyoruz.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              data-card-id={service.id}
              className={`group bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-8 hover:border-blue-500/50 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-500/20 cursor-pointer ${
                visibleCards.includes(service.id)
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-10 opacity-0'
              }`}
              style={{
                transitionDelay: `${index * 150}ms`
              }}
              onMouseEnter={() => setHoveredService(service.id)}
              onMouseLeave={() => setHoveredService(null)}
              tabIndex={0}
              role="article"
              aria-label={`${service.title} hizmeti detayları`}
            >
              {/* Service Image */}
              <div className="relative h-48 mb-6 rounded-xl overflow-hidden">
                <img 
                  src={service.image} 
                  alt={`${service.title} hizmet görseli`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                
                {/* Service Icon */}
                <div className="absolute top-4 left-4 w-12 h-12 bg-blue-500/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-blue-400/30">
                  <i className={`${service.icon} text-blue-400 text-xl`} aria-hidden="true"></i>
                </div>

                {/* Price Badge */}
                <div className="absolute bottom-4 right-4 px-3 py-1 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30">
                  <span className="text-blue-400 text-sm font-medium">{service.price}</span>
                </div>
              </div>

              {/* Service Content */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  {service.description}
                </p>

                {/* Features */}
                <div className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-400">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-green-400 mr-2 flex-shrink-0" aria-hidden="true"></i>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                <Button
                  onClick={() => scrollToSection('iletisim')}
                  variant="outline"
                  className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  aria-label={`${service.title} için teklif al`}
                >
                  Teklif Al
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </div>

              {/* Hover Glow Effect */}
              {hoveredService === service.id && (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 rounded-2xl pointer-events-none transition-opacity duration-300" />
              )}
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl border border-blue-400/20 p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Özel İhtiyaçlarınız İçin Çözüm Arıyor musunuz?
            </h3>
            <p className="text-gray-300 mb-6">
              Standart hizmetlerimiz dışında özel projeler için de çözüm üretiyoruz. 
              Size özel bir teklif hazırlayalım.
            </p>
            <Button 
              size="lg"
              onClick={() => scrollToSection('iletisim')}
            >
              Özel Proje Teklifi Al
              <i className="ri-rocket-line ml-2"></i>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
